/**
 * Screening service — single-name screening search + per-match row actions.
 *
 * Single screening is a LIVE search against the sanctions.io backend
 * (`GET /search/`), confirmed against staging 2026-06-08. The backend
 * returns the DRF list envelope `{count,next,previous,results}`; each result is
 * adapted from the wire (snake_case, arrays) into the `Match` shape the table +
 * detail drawer consume, so the page (results-layout.tsx) stays untouched.
 *
 * Backend contract:
 *   GET /search/?name=&country=&entity_type=&date_of_birth=&identifier=
 *                &min_score=&data_source=&manual_search=true   -> Paginated<SearchResultRaw>
 *   PATCH /search/report/{search_query_id}/                    -> async PDF (id = envelope search_query_id)
 *
 * ADR-009: the search params carry the screened name / DOB / identifier — they
 * are NEVER logged or breadcrumbed here, and the base_service error normalizer
 * does not echo the URL/params into ApiError.message.
 *
 * See ADR-010 — Service Layer Designed Fresh.
 */

import type {
  Match,
  EntityType,
  RiskLevel,
  AlternativeName,
  KnownLocation,
  ListingIdentifier,
  Position,
  Collection,
} from "@/app/(app)/screening/_data/types";
import { joinCountryNames, stringifyAdditional } from "./normalize_helpers";
import {
  screeningSearchSchema,
  type ScreeningSearchInput,
} from "@/app/(app)/screening/_schemas/screening-search";
import { addMonitoring, type MonitoringEntry } from "./monitoring_service";
import { searchAdverseMedia, type AdverseMediaSearchInput } from "./adverse_media_service";
import {
  listSearchConfigurations,
  type AdverseMediaKeyword,
  type AdverseMediaLanguage,
} from "./search_configurations_service";
import { api } from "./base_service";
import { apiEndpoints } from "@/src/config/api/endpoints";
import { apiTokenForSelection } from "@/components/watchlists";
import type { ApiError, Paginated, Result } from "./types";

export type { ScreeningSearchInput };

const PAGE_SIZE = 100;

const VALIDATION = (message: string): ApiError => ({
  status: 422,
  code: "validation",
  message,
  requestId: "client",
});

/** One `identifiers[]` entry (DataIdSerializer) — national id, passport, etc. */
export interface IdentifierRaw {
  identifier: string;
  type: string;
  country?: string;
  notes?: string;
}

export interface PositionRaw {
  title: string;
  organization?: string;
  country?: string;
  start_date?: string;
  end_date?: string;
  notes?: string;
}

/** One result as the backend sends it (snake_case; most fields are arrays). */
export interface SearchResultRaw {
  confidence_score: number;
  name: string;
  alt_names?: string[];
  entity_type?: string;
  address?: string[];
  country_residence?: string[];
  date_of_birth?: string | string[];
  place_of_birth?: string[];
  nationality?: string[];
  citizenship?: string[];
  data_source?: { name?: string; short_name?: string };
  program?: string[];
  remarks?: string;
  additional_information?: Record<string, unknown>;
  si_identifier?: string;
  /** Sibling of `data_source`, NOT nested under it — confirmed against the
   *  backend's DataEntry(Document)Serializer field list (both list
   *  `source_list_url` as a top-level field alongside `data_source`). */
  source_list_url?: string;
  /** Sibling of `data_source`/`source_list_url` — the entry-specific info page. */
  source_information_url?: string;
  entity_number?: number;
  first_import?: string;
  last_update?: string;
  data_hash?: string;
  /** Top-level (DataIdSerializer list) — national ids, passports, etc. */
  identifiers?: IdentifierRaw[];
  /** Top-level array (sibling of identifiers) — job titles/roles, PEP-style. */
  position?: PositionRaw[];
}

/**
 * The `GET /search/` envelope = the DRF list shape plus a nested `search` object
 * that carries the search-query UUID at `search.id` (e.g. "398ad40a-…") and a
 * top-level numeric `search_query_id`.
 *
 * The PDF report PATCH (`/search/report/{id}/`) keys on the UUID, NOT the numeric
 * id — VERIFIED end-to-end against staging 2026-06-11: a PATCH with the numeric
 * `search_query_id` (14887818) returns 404; the client production app keys the
 * report on the UUID (ref `downloads_menu_service` → `sanctionedEntities.search_id`
 * → `search.id`). So we stamp `search.id` onto each Match for exportMatch. (The
 * earlier "search has no id, use search_query_id" note was stale — the live
 * envelope now nests `search.id`.)
 */
interface SearchEnvelope extends Paginated<SearchResultRaw> {
  /** The search-query UUID the PDF report PATCH keys on. */
  search?: { id?: string } | null;
  /** Flat UUID fallback (if the backend ever flattens `search.id`). */
  search_id?: string;
  /** Numeric wire id — present on the envelope, but NOT the report key (404s). */
  search_query_id?: number | string;
}

/** Backend `entity_type` string → our `EntityType` vocab (best-effort).
 *  Exported so the batch + monitoring adapters normalize identically instead of
 *  casting the raw wire string `as EntityType` (which leaks invalid enums into
 *  the shared MatchDetail / entity-type icon). */
export function mapEntityType(raw: string | undefined): EntityType {
  switch ((raw ?? "").toLowerCase()) {
    case "individual":
      return "Individual";
    case "vessel":
      return "Vessel";
    case "aircraft":
      return "Aircraft";
    case "country":
      return "Country";
    case "organization":
    case "organisation":
    case "entity":
    case "legal entity":
    default:
      return "Organization";
  }
}

/**
 * Derive a Low/Med/High/Critical badge from `confidence_score`. The backend has
 * NO risk field on a search result (risk is a monitoring concept) — this is a
 * deliberate UI-side derivation; revisit if Alejandro specifies a rule.
 */
function deriveRiskLevel(score: number): RiskLevel {
  if (score >= 0.9) return "critical";
  if (score >= 0.8) return "high";
  if (score >= 0.6) return "medium";
  return "low";
}

/**
 * Derive our `Collection` token from the backend data_source `short_name`
 * (plus the adverse-media `entity_type` signal). Mirrors the live app's
 * category logic (ref entity_badge.tsx): the WIRE tokens are the bare "PEP" /
 * "INTERPOL" / "FBI" / "ADV-MEDIA" — NOT the UI SourceId aliases. Unknown or
 * empty short_names fall through to sanctions_lists (the backend's default
 * data_source), matching the entity_badge "else" branch and the
 * apiTokenForSelection pass-through. high_risk_jurisdictions has no per-result
 * short_name in the ref badge map; the token below is inferred from the
 * COLLECTIONS_LISTS filter catalog — confirm with Hector when a real high-risk
 * result short_name lands (unmatched values stay safely in sanctions_lists).
 */
export function deriveCollection(shortName: string | undefined, entityType?: string): Collection {
  if ((entityType ?? "") === "Media") return "adverse_media";
  switch ((shortName ?? "").toUpperCase()) {
    case "PEP":
      return "pep_lists";
    case "INTERPOL":
    case "FBI":
      return "criminal_watchlists";
    case "ADV-MEDIA":
      return "adverse_media";
    case "HIGH-RISK-JURISDICTIONS":
      return "high_risk_jurisdictions";
    default:
      return "sanctions_lists";
  }
}

function buildKnownLocations(raw: SearchResultRaw, countryDisplay: string): KnownLocation[] {
  const out: KnownLocation[] = [];
  for (const pob of raw.place_of_birth ?? []) out.push({ label: "Place of birth", value: pob });
  for (const addr of raw.address ?? []) out.push({ label: "Address", value: addr });
  if (countryDisplay) out.push({ label: "Country of residence", value: countryDisplay });
  return out;
}

function buildListingIdentifiers(raw: SearchResultRaw): ListingIdentifier[] {
  return (raw.identifiers ?? [])
    .filter((i) => i.identifier)
    .map((i) => ({ identifier: i.identifier, type: i.type, country: i.country, notes: i.notes }));
}

function buildPositions(raw: SearchResultRaw): Position[] {
  return (raw.position ?? [])
    .filter((p) => p.title)
    .map((p) => ({
      title: p.title,
      organization: p.organization,
      country: p.country,
      startDate: p.start_date,
      endDate: p.end_date,
      notes: p.notes,
    }));
}

/** The 4-digit year part of a dash-delimited date, if present. */
function getYear(date: string): string | undefined {
  return date.split("-").find((part) => part.length === 4);
}
/** "Full" date per the live app: 3 dash parts, a 4-digit year, and no "00" part. */
function isFullDate(date: string): boolean {
  const parts = date.split("-");
  return parts.length === 3 && parts.some((p) => p.length === 4) && !parts.includes("00");
}
/**
 * Normalize a backend `date_of_birth` to the Match.dob contract (ISO
 * YYYY-MM-DD, or just the year, or null). The backend types it as
 * string | string[] and emits partial values ("1952-00-00", "1952-10-00",
 * year-only "1952"). Mirrors the live app's isFullDate/getYear rule
 * (ref format_date.ts): a date is "full" only when it has 3 dash parts, a
 * 4-digit year, and no "00" part — otherwise fall back to the year. Unlike the
 * ref we keep full dates in ISO (Match.dob is ISO; formatDate() renders it).
 * Returns null when there is nothing usable, so the UI shows its empty state.
 */
export function normalizeDob(raw: string | string[] | null | undefined): string | null {
  const value = (Array.isArray(raw) ? raw[0] : raw) ?? "";
  if (!value) return null;
  if (isFullDate(value)) return value;
  return getYear(value) ?? null;
}

/**
 * normalizeDob for a DOB the user entered (search params, monitoring entries):
 * a YYYY-MM is kept as typed instead of being reduced to the year, so a
 * re-run recent search repeats the same query.
 */
export function normalizeInputDob(raw: string | string[] | null | undefined): string | null {
  const value = (Array.isArray(raw) ? raw[0] : raw) ?? "";
  if (/^\d{4}-(0[1-9]|1[0-2])$/.test(value)) return value;
  return normalizeDob(raw);
}

export function adaptMatch(raw: SearchResultRaw, searchId?: string): Match {
  // Collect + dedupe every country across residence/nationality/citizenship
  // (the live app's recover_countries behaviour). `residence` alone feeds the
  // "Country of residence" known-location below so that label stays accurate.
  const country = joinCountryNames([
    ...(raw.country_residence ?? []),
    ...(raw.nationality ?? []),
    ...(raw.citizenship ?? []),
  ]);
  const residence = joinCountryNames(raw.country_residence ?? []);
  const id =
    raw.si_identifier ??
    (raw.entity_number != null ? String(raw.entity_number) : undefined) ??
    raw.data_hash ??
    raw.name;
  const countryIso =
    raw.nationality?.[0] ?? raw.country_residence?.[0] ?? raw.citizenship?.[0] ?? undefined;
  return {
    id,
    siIdentifier: raw.si_identifier,
    searchId,
    score: raw.confidence_score,
    name: raw.name,
    type: mapEntityType(raw.entity_type),
    country,
    countryIso,
    // Partial / year-only backend dates ("1952-00-00", year-only) are
    // normalized to the Match.dob ISO contract via normalizeDob (its docstring).
    dob: normalizeDob(raw.date_of_birth),
    // The backend's `short_name` is an open catalog, so Match.source is typed
    // `string` (not the display-only SourceId union). Uppercased for display —
    // these are short mnemonic codes (OFAC, SDN, ADV-MEDIA, ...) that are always
    // uppercase by convention, but the live catalog / picker-id passthrough
    // (apiTokenForSelection's `?? id` fallback) doesn't guarantee that casing.
    source: (raw.data_source?.short_name ?? "").toUpperCase(),
    // Link target for the "Watchlist:" row (SourceLink) — the official list
    // page for this data source. A TOP-LEVEL field (sibling of data_source),
    // not nested under it. Undefined omits the link entirely rather than
    // rendering a dead `href="#"`.
    sourceListUrl: raw.source_list_url,
    sourceInformationUrl: raw.source_information_url,
    // Category derived from the data_source short_name (+ Media entity_type),
    // mirroring the live app's entity_badge logic. See deriveCollection.
    collection: deriveCollection(raw.data_source?.short_name, raw.entity_type),
    firstImported: raw.first_import?.slice(0, 10),
    lastUpdated: (raw.last_update ?? raw.first_import ?? "").slice(0, 10),
    riskLevel: deriveRiskLevel(raw.confidence_score),
    alternativeNames: (raw.alt_names ?? []).map<AlternativeName>((name) => ({
      name,
      kind: "alias",
    })),
    knownLocations: buildKnownLocations(raw, residence),
    identifiers: buildListingIdentifiers(raw),
    positions: buildPositions(raw),
    remarks: raw.remarks ? [raw.remarks] : [],
    additionalInformation: stringifyAdditional(raw.additional_information),
  };
}

/**
 * UI entity-type vocab → backend `/search/` `entity_type` token. Only the org
 * type diverges: the UI label is "Organization" (AGENTS.md vocabulary) but the
 * backend token is "Entity" (confirmed against the client production app's
 * select_entity_type). The other four pass through unchanged. Translate here at
 * the service boundary — same pattern as `apiTokenForSelection` for data_source.
 * (Monitoring will need the same map when it's wired to the live backend.)
 */
const ENTITY_TYPE_TO_API: Record<string, string> = {
  Individual: "Individual",
  Organization: "Entity",
  Vessel: "Vessel",
  Aircraft: "Aircraft",
  Country: "Country",
};

/**
 * Build the `data_source` token list from the picker's selection ids.
 *
 * The picker selects by internal ids (kebab source ids like `us-ofac-sdn`,
 * `coll:`-prefixed collection ids); the backend wants `short_name`s + collection
 * keywords. `apiTokenForSelection` translates each (and passes real short_names
 * through, so this stays correct once the picker is fed live `/sources/` data).
 * An empty selection means the backend default — all sanctions lists.
 */
function buildDataSource(watchlists: string[] | undefined): string {
  const tokens = (watchlists ?? []).map(apiTokenForSelection).filter(Boolean);
  return tokens.length > 0 ? tokens.join(",") : "SANCTIONS-LISTS";
}

/** Build the `GET /search/` query params from the validated input. */
function buildSearchParams(input: ScreeningSearchInput): Record<string, string | number> {
  return {
    name: input.query,
    country: (input.countries ?? []).join(","),
    entity_type: input.entityType ? (ENTITY_TYPE_TO_API[input.entityType] ?? input.entityType) : "",
    date_of_birth: input.dob ?? "",
    identifier: input.identifier ?? "",
    min_score: input.confidenceScore / 100,
    data_source: buildDataSource(input.watchlists),
    manual_search: "true",
    page_size: PAGE_SIZE,
  };
}

const ADV_MEDIA_ID = "coll:adverse-media";

function splitWatchlists(watchlists: string[]): {
  hasAdvMedia: boolean;
  otherWatchlists: string[];
} {
  return {
    hasAdvMedia: watchlists.some((id) => id === ADV_MEDIA_ID),
    otherWatchlists: watchlists.filter((id) => id !== ADV_MEDIA_ID),
  };
}

export type ActiveCollections = {
  hasSanctions: boolean;
  hasAdvMedia: boolean;
  /** Validated input with adverse media stripped from `watchlists` — pass to `fetchSanctions`. */
  sanctionsInput: ScreeningSearchInput;
  /** Validated input, unmodified — pass to `fetchAdvMedia`. */
  adverseMediaInput: ScreeningSearchInput;
};

/**
 * Validate the search input and decide which of the two collections
 * (sanctions/watchlists, adverse media) this search actually covers, based on
 * the selected watchlists:
 *   1. Only `coll:adverse-media` selected → adverse media only.
 *   2. Only other watchlists (or none) selected → sanctions only.
 *   3. Both → both (the caller shows tabs and links both searches below).
 *
 * The caller (MatchesPanel) uses this to decide whether to render tabs and
 * which of `fetchSanctions`/`fetchAdvMedia` to wire up for paginated fetching.
 */
export function resolveActiveCollections(input: ScreeningSearchInput): Result<ActiveCollections> {
  const parsed = screeningSearchSchema.safeParse(input);
  if (!parsed.success) {
    return { ok: false, error: VALIDATION(parsed.error.issues[0]?.message ?? "Invalid search") };
  }
  const { hasAdvMedia, otherWatchlists } = splitWatchlists(parsed.data.watchlists ?? []);
  const hasSanctions = !hasAdvMedia || otherWatchlists.length > 0;
  return {
    ok: true,
    value: {
      hasSanctions,
      hasAdvMedia,
      sanctionsInput: { ...parsed.data, watchlists: otherWatchlists },
      adverseMediaInput: parsed.data,
    },
  };
}

/**
 * One page of `GET /search/` (sanctions/watchlists). `linkedSearchReference`
 * correlates this call with a parallel adverse-media search for the combined
 * PDF report, when both collections are active for the same search.
 */
export async function fetchSanctions(
  input: ScreeningSearchInput,
  page: number,
  signal?: AbortSignal,
  linkedSearchReference?: string,
): Promise<Result<{ matches: Match[]; count: number; next: string | null; searchId?: string }>> {
  try {
    const params = buildSearchParams(input);
    params.page = page;
    if (linkedSearchReference) {
      params.linked_search_reference = linkedSearchReference;
    }
    const data = await api.get<SearchEnvelope>(apiEndpoints.screening().search(), {
      params,
      signal,
    });
    const rawSearchId = data.search?.id ?? data.search_id;
    const searchId = rawSearchId != null ? String(rawSearchId) : undefined;
    const matches = (data.results ?? [])
      .map((raw) => adaptMatch(raw, searchId))
      .sort((a, b) => b.score - a.score);
    return {
      ok: true,
      value: { matches, count: data.count ?? matches.length, next: data.next ?? null, searchId },
    };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

/** One page of `GET search/media` (adverse media) — same correlation contract as `fetchSanctions`. */
export async function fetchAdvMedia(
  input: ScreeningSearchInput,
  page: number,
  signal?: AbortSignal,
  linkedSearchReference?: string,
): Promise<Result<{ matches: Match[]; count: number; next: string | null }>> {
  const configResult = await listSearchConfigurations();
  const companyConfig = configResult.ok
    ? configResult.value.find((c) => c.type === "company" && c.adverseMedia !== null)
    : undefined;
  const am = companyConfig?.adverseMedia;

  // Only the search's own countries — the backend combines them with the
  // company config countries (`null` / `[]` / list).
  const countries = input.countries ?? [];

  const advInput: AdverseMediaSearchInput = {
    name: input.query,
    manual_search: true,
    page,
    ...(linkedSearchReference ? { linkedSearchReference } : {}),
    ...(am?.keywords?.length ? { keywords: am.keywords as AdverseMediaKeyword[] } : {}),
    ...(countries.length ? { countries } : {}),
    ...(am?.language?.length ? { languages: am.language as AdverseMediaLanguage[] } : {}),
    // daysBack (time horizon) has no field in the contract, same as minScore —
    // rendered/persisted in settings, but never sent to a real search.
  };

  const res = await searchAdverseMedia(advInput, signal);
  if (!res.ok) return res;
  return {
    ok: true,
    value: { matches: res.value.results, count: res.value.count, next: res.value.next },
  };
}

/**
 * Trigger PDF report generation for a search by its UUID. Used when there is
 * no selected match (e.g. zero-results searches) — the report covers the
 * whole search regardless of how many matches it produced.
 */
export async function exportSearchById(searchId: string): Promise<Result<void>> {
  try {
    await api.patch<void>(apiEndpoints.screening().report(searchId));
    return { ok: true, value: undefined };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

/**
 * Add a screened match to the monitoring list. Bridges to monitoring_service so
 * the existing add flow is reused (monitoring wiring lands in its own step).
 *
 * Backend mapping (eventual): POST monitoring/ -> MonitoringEntry
 */
export async function addMatchToMonitoring(match: Match): Promise<Result<MonitoringEntry>> {
  return addMonitoring({
    name: match.name,
    dob: match.dob,
    entityType: match.type,
    watchlists: match.source ? [match.source] : undefined,
    countries: match.countryIso ? [match.countryIso] : undefined,
  });
}

/**
 * Export the match's search as a PDF. The backend kicks off async PDF
 * generation via PATCH /search/report/{search_query_uuid}/ (no body) and
 * notifies via the notifications menu — so success is fire-and-forget
 * (`Result<void>`). The uuid is the search-query id stamped on every match by
 * `fetchSanctions`/`fetchAdvMedia`; the report is per-search (not per-row), so exporting any row
 * reports the whole search — matching the live app. Matches without a `searchId`
 * (fixture/batch results until they wire their own uuid) surface an honest
 * validation error rather than a silent no-op.
 */
export async function exportMatch(match: Match): Promise<Result<void>> {
  if (!match.searchId) {
    return { ok: false, error: VALIDATION("Export isn’t available for this result") };
  }
  try {
    await api.patch<void>(apiEndpoints.screening().report(match.searchId));
    return { ok: true, value: undefined };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}
